import type { CatalogEvent, CatalogPlayer } from "@livcg/schema";

/** Who is on the pitch right now: the starting XI with the team's logged
 *  substitutions replayed in game-clock order (equal times keep log order).
 *  A substitute who came on can go off again; a player who went off cannot
 *  come back, so `available` is the bench minus everyone already used. */
export function pitchState(
  starters: CatalogPlayer[],
  bench: CatalogPlayer[],
  events: readonly CatalogEvent[],
  teamId: string | undefined,
): { onField: CatalogPlayer[]; available: CatalogPlayer[] } {
  const byId = new Map([...starters, ...bench].map((p) => [p.id, p]));
  const field = new Map(starters.map((p) => [p.id, p]));
  const used = new Set<string>();

  const at = (e: CatalogEvent) => e.minute * 60 + e.second;
  const subs = events
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => e.type === "substitution" && e.teamId === teamId)
    .sort((a, b) => at(a.e) - at(b.e) || a.i - b.i);

  for (const { e } of subs) {
    field.delete(e.playerId);
    used.add(e.playerId);
    const inPlayer = e.assistPlayerId ? byId.get(e.assistPlayerId) : undefined;
    if (inPlayer) {
      field.set(inPlayer.id, inPlayer);
      used.add(inPlayer.id);
    }
  }

  return {
    onField: [...field.values()],
    available: bench.filter((p) => !used.has(p.id) && !field.has(p.id)),
  };
}
